import { toast } from 'react-toastify';
import api from './api';

/**
 * Common API utility functions to reduce code duplication across context files
 */

/**
 * Get authentication token from localStorage
 * @returns {string|null} Token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * GET request helper using the main api instance
 * @param {string} endpoint - API endpoint
 * @param {boolean} showToast - Whether to show error toast
 * @returns {Promise<Object>} Response data or error object
 */
export const apiGet = async (endpoint, showToast = true) => {
  try {
    const response = await api.get(endpoint);
    return { success: true, data: response.data };
  } catch (error) {
    console.error(`API GET request failed for ${endpoint}:`, error);
    
    if (showToast && error.response?.status !== 401) {
      toast.error(error.response?.data?.message || error.message || 'An error occurred');
    }
    
    return { success: false, error: error.message };
  }
};

/**
 * POST request helper using the main api instance
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {boolean} showToast - Whether to show error toast
 * @returns {Promise<Object>} Response data or error object
 */
export const apiPost = async (endpoint, data, showToast = true) => {
  try {
    const response = await api.post(endpoint, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error(`API POST request failed for ${endpoint}:`, error);
    
    if (showToast && error.response?.status !== 401) {
      toast.error(error.response?.data?.message || error.message || 'An error occurred');
    }
    
    return { success: false, error: error.message };
  }
};

/**
 * PUT request helper using the main api instance
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {boolean} showToast - Whether to show error toast
 * @returns {Promise<Object>} Response data or error object
 */
export const apiPut = async (endpoint, data, showToast = true) => {
  try {
    const response = await api.put(endpoint, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error(`API PUT request failed for ${endpoint}:`, error);
    
    if (showToast && error.response?.status !== 401) {
      toast.error(error.response?.data?.message || error.message || 'An error occurred');
    }
    
    return { success: false, error: error.message };
  }
};

/**
 * DELETE request helper using the main api instance
 * @param {string} endpoint - API endpoint
 * @param {boolean} showToast - Whether to show error toast
 * @returns {Promise<Object>} Response data or error object
 */
export const apiDelete = async (endpoint, showToast = true) => {
  try {
    const response = await api.delete(endpoint);
    return { success: true, data: response.data };
  } catch (error) {
    console.error(`API DELETE request failed for ${endpoint}:`, error);
    
    if (showToast && error.response?.status !== 401) {
      toast.error(error.response?.data?.message || error.message || 'An error occurred');
    }
    
    return { success: false, error: error.message };
  }
};

/**
 * Handle API/Local storage fallback pattern common across contexts
 * @param {Function} apiFunction - Function that makes API call
 * @param {Function} localFunction - Function that handles local storage
 * @param {boolean} isOnline - Whether user is online/authenticated
 * @param {string} errorMessage - Custom error message
 * @returns {Promise<Object>} Result object
 */
export const handleApiOrLocal = async (apiFunction, localFunction, isOnline, errorMessage = 'Operation failed') => {
  try {
    if (isOnline) {
      const result = await apiFunction();
      if (result.success) {
        return result;
      }
      throw new Error(result.error || errorMessage);
    } else {
      return await localFunction();
    }
  } catch (error) {
    console.error(errorMessage, error);
    toast.error(error.message || errorMessage);
    return { success: false, error: error.message };
  }
};

/**
 * Common pattern for loading data with local/API fallback
 * @param {Function} apiFunction - Function to call API
 * @param {Function} localFunction - Function to get local data
 * @param {Function} setStateFunction - Function to set state
 * @param {boolean} isOnline - Whether user is authenticated
 * @param {string} dataType - Type of data being loaded (for error messages)
 */
export const loadDataWithFallback = async (apiFunction, localFunction, setStateFunction, isOnline, dataType = 'data') => {
  try {
    if (isOnline) {
      const result = await apiFunction();
      if (result.success && result.data) {
        setStateFunction(result.data);
      }
    } else {
      const localData = localFunction();
      if (localData) {
        setStateFunction(localData);
      }
    }
  } catch (error) {
    console.error(`Error loading ${dataType}:`, error);
    // Don't show toast for loading errors as they're usually silent
  }
};

/**
 * Validate required fields helper
 * @param {Object} data - Data object to validate
 * @param {Array} requiredFields - Array of required field names
 * @returns {Object} Validation result
 */
export const validateRequiredFields = (data, requiredFields) => {
  const missingFields = requiredFields.filter(field => {
    const value = data[field];
    return value === undefined || value === null || value === '' || 
           (Array.isArray(value) && value.length === 0);
  });

  return {
    isValid: missingFields.length === 0,
    missingFields,
    message: missingFields.length > 0 
      ? `Missing required fields: ${missingFields.join(', ')}`
      : ''
  };
};

/**
 * Format error message for display
 * @param {Error|string} error - Error object or message
 * @returns {string} Formatted error message
 */
export const formatErrorMessage = (error) => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error && error.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
};